import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import formatDate from 'date-fns/format';
import classNames from 'classnames';

import sharedStyle from 'app/pages/BlacklistPage/style.scss';
import MarketTag from 'app/components/MarketTag';
import OpponentAvatar from 'app/components/OpponentAvatar';
import { getBlacklistPlayerTeamAsOpponent, getStatusTagColor } from 'app/pages/BlacklistPage/utils';
import UserAvatar from 'app/components/UserAvatar';
import Button from 'app/components/Button';

import style from './style.scss';

interface Props {
  playerItems: BlacklistPlayerHistoryItem[];
  type: 'history' | 'player_list';
  className?: string;
}

const BlacklistPlayerItems: FC<Props> = ({ playerItems, type, className }) => {
  const [t] = useTranslation();

  const contractReasons = type === 'player_list';
  // We store created_at dates because there is no id
  const [expandedReasons, setExpandedReasons] = useState<string[]>([]);

  return (
    <div className={classNames(style.content, className)}>
      {playerItems.map(item => {
        const contractPlayerReason =
          contractReasons && item.reason?.length && item.reason.length > 90;
        return (
          <div className={style.historyItem} key={item.created_at}>
            <div className={style.infoLine}>
              <MarketTag color={getStatusTagColor(item.status)} className={sharedStyle.statusTag}>
                {t(`blacklist-status-${item.status ?? 'none'}`)}
              </MarketTag>
              {item.created_at ? formatDate(new Date(item.created_at), 'MM/dd/yyyy') : '-'}
              {type === 'player_list' && <>&nbsp; &bull; {item.name}</>}
              {item.team && (
                <>
                  &nbsp; &bull;
                  <div className={sharedStyle.playerTeam}>
                    <OpponentAvatar
                      opponent={getBlacklistPlayerTeamAsOpponent(item.team)}
                      size={26}
                      className={sharedStyle.playerTeamIcon}
                    />{' '}
                    {item.team.name}
                  </div>
                </>
              )}
            </div>
            <div className={style.reason}>
              {(!contractPlayerReason || expandedReasons.includes(item.created_at)) && item.reason}
              {contractPlayerReason && !expandedReasons.includes(item.created_at) && (
                <>
                  {item.reason && `${item.reason.slice(0, 85)}...`}
                  <Button
                    variation="empty"
                    className={style.seeMoreReasonButton}
                    onClick={() => setExpandedReasons(reasons => [...reasons, item.created_at])}
                  >
                    See more
                  </Button>
                </>
              )}
            </div>

            {item.reporter && (
              <div className={style.userLine}>
                <span className={style.listedBy}>{t('blacklist-history-listed_by')}</span>
                <UserAvatar user={item.reporter} size={15} />
                {item.reporter.name}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

BlacklistPlayerItems.displayName = 'BlacklistPlayerItems';

export default BlacklistPlayerItems;
